import './loanDocumentListPage.scss';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import useNewLoanDocumentStore from '../../../store/loanDocumentStore/useNewLoanDocumentStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { setDrawerOpen } = useNavigationPaneStore();
  const { handleSubmit } = useForm();

  const {
    computed: {
      hasNotProvidedDocuments,
      hasProvidedGAPDocuments,
      hasProvidedRICDocuments,
      hasProvidedVSCDocuments,
      numberOfGAPDocuments,
      numberOfRICDocuments,
      numberOfVSCDocuments,
      percent,
    },
  } = useNewLoanDocumentStore();

  const previousStep = (): void => {
    navigate('/loan-documents');
  };

  const formSubmit = (): void => {
    navigate('/loan-documents/current-mileage');
  };

  return (
    <div className="loan-document-list-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.loanDocumentList.header.root}>
            <button
              aria-label="Previous step"
              data-testid={
                locator.pages.loanDocumentList.header.previousStepButton
              }
              type="button"
              onClick={previousStep}
            >
              <KeyboardBackspaceRoundedIcon />
            </button>
            <button
              aria-label="Toggle drawer"
              data-testid={locator.pages.loanDocumentList.header.drawerButton}
              type="button"
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              <MenuRoundedIcon />
            </button>
          </header>

          <div className="progress-container" role="complementary">
            <LinearProgress
              aria-label="Loan Documents completed percentage"
              data-testid={locator.pages.loanDocumentList.progress.root}
              value={percent}
              variant="determinate"
            />
          </div>

          <main>
            <form
              data-testid={locator.pages.loanDocumentList.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.loanDocumentList.form.h1}>
                  {t('LoanDocumentListPage.title')}
                </h1>
                <h2 data-testid={locator.pages.loanDocumentList.form.h2}>
                  {t('LoanDocumentListPage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <ul>
                    <li
                      data-testid={
                        locator.pages.loanDocumentList.form.vscListItemRoot
                      }
                    >
                      <Link
                        className={hasProvidedVSCDocuments ? 'provided' : ''}
                        data-testid={
                          locator.pages.loanDocumentList.form.vscListItemLink
                        }
                        to="/loan-documents/vsc-upload"
                      >
                        <div>
                          {hasProvidedVSCDocuments ? (
                            <CheckBoxIcon className="provided" />
                          ) : (
                            <DriveFolderUploadOutlinedIcon />
                          )}
                          <div className="title">
                            <h3>{t('LoanDocumentListPage.vsc')}</h3>
                            <span>
                              {hasProvidedVSCDocuments
                                ? t('LoanDocumentListPage.edit')
                                : t('LoanDocumentListPage.upload')}
                            </span>
                          </div>
                          <span className="subtitle">
                            {hasProvidedVSCDocuments
                              ? t('LoanDocumentListPage.numberOfDocs', {
                                  count: numberOfVSCDocuments,
                                })
                              : t('LoanDocumentListPage.tapToUpload')}
                          </span>
                        </div>
                      </Link>
                    </li>

                    <li
                      data-testid={
                        locator.pages.loanDocumentList.form.gapListItemRoot
                      }
                    >
                      <Link
                        className={hasProvidedGAPDocuments ? 'provided' : ''}
                        data-testid={
                          locator.pages.loanDocumentList.form.gapListItemLink
                        }
                        to="/loan-documents/gap-upload"
                      >
                        <div>
                          {hasProvidedGAPDocuments ? (
                            <CheckBoxIcon className="provided" />
                          ) : (
                            <DriveFolderUploadOutlinedIcon />
                          )}
                          <div className="title">
                            <h3>{t('LoanDocumentListPage.gap')}</h3>
                            <span>
                              {hasProvidedGAPDocuments
                                ? t('LoanDocumentListPage.edit')
                                : t('LoanDocumentListPage.upload')}
                            </span>
                          </div>
                          <span className="subtitle">
                            {hasProvidedGAPDocuments
                              ? t('LoanDocumentListPage.numberOfDocs', {
                                  count: numberOfGAPDocuments,
                                })
                              : t('LoanDocumentListPage.tapToUpload')}
                          </span>
                        </div>
                      </Link>
                    </li>

                    <li
                      data-testid={
                        locator.pages.loanDocumentList.form.ricListItemRoot
                      }
                    >
                      <Link
                        className={hasProvidedRICDocuments ? 'provided' : ''}
                        data-testid={
                          locator.pages.loanDocumentList.form.ricListItemLink
                        }
                        to="/loan-documents/ric-upload"
                      >
                        <div>
                          {hasProvidedRICDocuments ? (
                            <CheckBoxIcon className="provided" />
                          ) : (
                            <DriveFolderUploadOutlinedIcon />
                          )}
                          <div className="title">
                            <h3>{t('LoanDocumentListPage.ric')}</h3>
                            <span>
                              {hasProvidedRICDocuments
                                ? t('LoanDocumentListPage.edit')
                                : t('LoanDocumentListPage.upload')}
                            </span>
                          </div>
                          <span className="subtitle">
                            {hasProvidedRICDocuments
                              ? t('LoanDocumentListPage.numberOfDocs', {
                                  count: numberOfRICDocuments,
                                })
                              : t('LoanDocumentListPage.tapToUpload')}
                          </span>
                        </div>
                      </Link>
                    </li>
                  </ul>
                </div>

                <div style={{ marginBottom: '3rem' }}>
                  <LoadingButton
                    color="primary"
                    data-testid={
                      locator.pages.loanDocumentList.header.submitButton
                    }
                    disabled={hasNotProvidedDocuments}
                    startIcon={<></>}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {t('LoanDocumentListPage.continue')}
                  </LoadingButton>
                </div>
              </div>
              <footer data-testid={locator.pages.loanDocumentList.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'LoanDocumentListPage';
